import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useFormContext } from "react-hook-form";
import { SchemaField, type FieldConfig } from "../../../components/forms/SchemaField";
import { FormSection } from "../../../components/forms/FormSection";
import { useAuth } from "../../../context/AuthContext";
import type { Role } from "@event-planner/shared";
import { useIsPhone } from "../../../hooks/useIsPhone";
import type { UserEditFormData } from "../../../schemas/userProfileSchema";

const ASSIGNABLE_ROLES: Role[] = [
  "companion",
  "invitee",
  "internal",
  "organizer",
  "admin",
];

const ROLE_OPTIONS = ASSIGNABLE_ROLES.map((r) => ({
  value: r,
  label: r.charAt(0).toUpperCase() + r.slice(1),
}));

interface ProfileTabProps {
  /**
   * Create-page variant (ADR-169 Stage 2 folded the ADR-125 conversion
   * in): the role select is enabled for organizers too — at creation no
   * role has been granted yet, so the edit page's admin-only lock doesn't
   * apply. Organizers still can't assign admin (option filtered here,
   * enforced server-side by createContact).
   */
  createMode?: boolean;
  /** data-testid prefix (e.g. "user-create") for E2E selector stability. */
  testIdPrefix?: string;
}

/**
 * Pure section component. The owning page (`UserDetailPage` or
 * `UserCreatePage`) provides the RHF form context, handles loading, and
 * dispatches the submit. This component is just the layout for the
 * Profile slice of the unified form.
 */
export function ProfileTab({ createMode = false, testIdPrefix }: ProfileTabProps) {
  const isPhone = useIsPhone();
  const { control } = useFormContext<UserEditFormData>();
  const { role: callerRole } = useAuth();
  const isCallerAdmin = callerRole === "admin";

  const tid = (slug: string) =>
    testIdPrefix ? `${testIdPrefix}-${slug}` : undefined;

  const nameFields: FieldConfig<UserEditFormData>[] = [
    {
      name: "honorificPrefix",
      label: "Prefix",
      type: "text",
      placeholder: "Dr., Prof., Hon.",
      testId: tid("honorificPrefix-input"),
    },
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      required: true,
      testId: tid("firstName-input"),
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      required: true,
      testId: tid("lastName-input"),
    },
    {
      name: "honorificSuffix",
      label: "Suffix",
      type: "text",
      placeholder: "PhD, J.D., III",
      testId: tid("honorificSuffix-input"),
    },
  ];

  const professionalFields: FieldConfig<UserEditFormData>[] = [
    {
      name: "title",
      label: "Title",
      type: "text",
      placeholder: "e.g. Managing Partner",
      testId: tid("title-input"),
    },
    {
      name: "organization",
      label: "Organization",
      type: "text",
      testId: tid("organization-input"),
    },
    { name: "bio", label: "Bio", type: "textarea", rows: 3, testId: tid("bio-input") },
  ];

  const canPickRole = isCallerAdmin || createMode;
  const roleOptions = isCallerAdmin
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((r) => r.value !== "admin");

  return (
    <Paper sx={{ p: isPhone ? 2 : 3 }}>
      <FormSection title="Name">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          {nameFields.map((field) => (
            <Box key={field.name} sx={{ flex: 1 }}>
              <SchemaField config={field} control={control} />
            </Box>
          ))}
        </Box>
      </FormSection>

      <FormSection title="Professional">
        {professionalFields.map((field) => (
          <SchemaField key={field.name} config={field} control={control} />
        ))}
      </FormSection>

      <FormSection title="Role">
        <SchemaField
          config={{
            name: "role",
            label: "Role",
            type: "select",
            options: roleOptions,
            disabled: !canPickRole,
            required: true,
            testId: tid("role-select"),
            helperText: createMode
              ? "Global role across the platform."
              : isCallerAdmin
                ? "Global role across the platform. Changing it updates this user's role on every event they belong to."
                : "Global role across the platform. Only an admin can change this.",
          }}
          control={control}
        />
      </FormSection>
    </Paper>
  );
}
